import IconButton from 'Components/Atoms/IconButton/IconButton';
import OutlineAdd from 'Components/Atoms/OutlineIcons/OutlineAdd';
import { RHFInputCurrency } from 'Components/Organisms/HookForm';
import Icon from 'Components/Atoms/Icon/Icon';
import useLocale from 'Hooks/useLocale';
import PropTypes from 'prop-types';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { cn } from 'Utils/cn';
import { getCurrencySymbol } from 'Utils/formatters';
import Button from 'Components/Atoms/Button/Button';
import SelectSingleTax from 'Components/Organisms/SelectSingleTax/SelectSingleTax';

export default function InvoicesTaxes(props) {
    const { name = 'taxes', className, currency = 'EUR', disabled = false } = props;

    const { t } = useTranslation('invoices');
    const { control, watch } = useFormContext();
    const locale = useLocale();

    const { fields, append, remove } = useFieldArray({
        control,
        name,
    });

    const watchedTaxes = watch(name);
    const amount = watch('amount') || 0;
    const amountSubtotal = watch('amount_subtotal') || 0;
    const amountReimbursable = watch('amount_reimbursable') || 0;
    const currencySymbol = getCurrencySymbol(locale, currency);

    // Calcular la suma de todos los impuestos
    const totalTaxes = (watchedTaxes || []).reduce((sum, tax) => sum + (Number(tax.amount_tax) || 0), 0);

    // Impuestos sin asignar = amount - amount_subtotal - suma de taxes - amount_reimbursable
    const rawUnassignedTaxes = Number(amount) - Number(amountSubtotal) - totalTaxes - Number(amountReimbursable);
    const unassignedTaxes = Math.abs(rawUnassignedTaxes) < 0.001 ? 0 : rawUnassignedTaxes;

    const handleAddTax = () => {
        append({
            tax_id: '',
            amount_base: Math.round(amountSubtotal * 100) / 100,
            amount_tax: Math.round(unassignedTaxes * 100) / 100,
        });
    };

    // Obtener los tax_ids ya seleccionados para evitar duplicados
    const selectedTaxIds = (watchedTaxes || []).map((tax) => tax.tax_id).filter(Boolean);

    return (
        <div className={cn('col-span-full', className)}>
            {fields.map((field, index) => {
                const isLast = index === fields.length - 1;

                return (
                    <div key={field.id} className={cn('grid grid-cols-3 items-start gap-2', { 'mb-4': !isLast })}>
                        <Controller
                            name={`${name}.${index}.tax_id`}
                            control={control}
                            render={({ field: taxField, fieldState: { error } }) => (
                                <SelectSingleTax
                                    value={taxField.value}
                                    onChange={taxField.onChange}
                                    placeholder={t('select-tax')}
                                    label={t('tax')}
                                    disabled={disabled}
                                    excludeIds={selectedTaxIds.filter((id) => id !== taxField.value)}
                                    error={error?.message}
                                />
                            )}
                        />

                        <RHFInputCurrency
                            name={`${name}.${index}.amount_base`}
                            prefix={currencySymbol}
                            label={t('tax-base')}
                            disabled={disabled}
                        />

                        <div className="flex items-start gap-2">
                            <div className="flex-1">
                                <RHFInputCurrency
                                    name={`${name}.${index}.amount_tax`}
                                    prefix={currencySymbol}
                                    label={t('amount')}
                                    disabled={disabled}
                                />
                            </div>

                            {!disabled && (
                                <div className="h-11 flex items-center">
                                    <IconButton
                                        icon={<Icon name="trash" />}
                                        iconSize={18}
                                        size="small"
                                        variant="ghost"
                                        onClick={() => remove(index)}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}

            {!disabled && (
                <>
                    {unassignedTaxes !== 0 && (
                        <div className={cn('bg-warning/10 rounded-md', { 'mt-4': fields.length > 0 })} role="alert">
                            <div className="flex items-center gap-3 p-4">
                                <div className="shrink-0">
                                    <Icon name="circle-alert" color="currentColor" className="text-warning" />
                                </div>
                                <div className="grow text-dark-blue text-sm">
                                    {t('unassigned-taxes-tooltip', { amount: `${currencySymbol}${(Math.round(unassignedTaxes * 100) / 100).toFixed(2)}` })}
                                </div>
                            </div>
                        </div>
                    )}
                    <Button
                        variant="ghost"
                        size="small"
                        onClick={handleAddTax}
                        className="mt-2"
                    >
                        <OutlineAdd size={18} />
                        {t('add-tax')} {unassignedTaxes !== 0 && `(${t('unassigned-taxes')}: ${currencySymbol}${(Math.round(unassignedTaxes * 100) / 100).toFixed(2)})`}
                    </Button>
                </>
            )}
        </div>
    );
}

InvoicesTaxes.propTypes = {
    name: PropTypes.string,
    className: PropTypes.string,
    currency: PropTypes.string,
    disabled: PropTypes.bool,
};
